import type { RequestPayload } from "@inertiajs/core";
import { Accordion, Button, Fieldset, Group, Stack, Text, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconSend } from "@tabler/icons-react";
import { gradeTranslationsType3 } from "../../../models/enums/grade";
import type { EducationReport } from "../../../models/reports/educationReport";
import type { EducationReportTranslations } from "../../../models/reports/educationReportTranslations";
import { SaveButton } from "../../button/SaveButton";
import {
  getCoachSupportFields,
  getMethodologicalFields,
  getProfessionalFields,
  getReceivedTrainingFields,
  getSelfFields,
  getSocialFields,
} from "../educationReport/GradingFields";
import { RemarkedGradingSection } from "../educationReport/RemarkedGradingSection";

type EducationReportSelfAssessmentProps = {
  educationReport: EducationReport;
  translations: EducationReportTranslations;
  onSave: (values: RequestPayload) => void;
  onComplete: (values: RequestPayload) => void;
};

export type EducationReportSelfAssessmentFormType = Pick<EducationReport, "self_assessment">;

export function EducationReportSelfAssessmentForm({
  educationReport,
  translations,
  onComplete,
  onSave,
}: EducationReportSelfAssessmentProps) {
  const maxLength = educationReport.report_configuration.data.max_remark_chars;
  const lengthValidation = (value: string, fraction = 1) => {
    return value?.length > maxLength / fraction ? `zu lange (max ${maxLength} Zeichen)` : null;
  };

  const form = useForm<EducationReportSelfAssessmentFormType>({
    mode: "uncontrolled",
    initialValues: {
      self_assessment: {
        ...educationReport.self_assessment,
      },
    },
    validateInputOnChange: true,
    validate: {
      self_assessment: {
        apprentice_assessment: {
          received_training: {
            remarks: (value) => lengthValidation(value),
          },
          coach_support: {
            remarks: (value) => lengthValidation(value),
          },
        },
      },
    },
  });

  const professionalFields = getProfessionalFields(translations);
  const methodologicalFields = getMethodologicalFields(translations);
  const socialFields = getSocialFields(translations);
  const selfFields = getSelfFields(translations);
  const receivedTrainingFields = getReceivedTrainingFields(translations);
  const coachSupportFields = getCoachSupportFields();

  const accordionFields = [
    {
      title: translations.professional_competence.title,
      key: "professional_competence",
      baseNumber: 1,
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          path={key}
          baseNumber={baseNumber}
          form={form}
          baseKey="self_assessment"
          fields={professionalFields}
          remarksKey="remarks"
          remarksMaxLength={maxLength}
        />
      ),
    },
    {
      title: translations.methodological_competence.title,
      key: "methodological_competence",
      baseNumber: 2,
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          path={key}
          baseNumber={baseNumber}
          form={form}
          baseKey="self_assessment"
          fields={methodologicalFields}
          remarksKey="remarks"
          remarksMaxLength={maxLength}
        />
      ),
    },
    {
      title: translations.social_competence.title,
      key: "social_competence",
      baseNumber: 3,
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          baseNumber={baseNumber}
          form={form}
          baseKey="self_assessment"
          fields={socialFields}
          path={key}
          remarksKey="remarks"
          remarksMaxLength={maxLength}
        />
      ),
    },
    {
      title: translations.self_competence.title,
      key: "self_competence",
      baseNumber: 4,
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          path={key}
          baseNumber={baseNumber}
          form={form}
          baseKey="self_assessment"
          fields={selfFields}
          remarksKey="remarks"
          remarksMaxLength={maxLength}
        />
      ),
    },
    {
      title: translations.apprentice_assessment.title,
      key: "apprentice_assessment",
      baseNumber: 7,
      render: (baseNumber: number, key: string) => (
        <Stack>
          <RemarkedGradingSection
            title={`${baseNumber}.1 ${translations.apprentice_assessment.received_training.title}`}
            disableNumbering
            path={`${key}.received_training`}
            baseNumber={baseNumber}
            form={form}
            remarksKey="remarks"
            baseKey="self_assessment"
            remarksMaxLength={maxLength}
            fields={receivedTrainingFields}
            gradeTranslations={gradeTranslationsType3}
          />
          <RemarkedGradingSection
            title={`${baseNumber}.2 ${translations.apprentice_assessment.coach_support.title}`}
            disableNumbering
            path={`${key}.coach_support`}
            baseNumber={baseNumber}
            form={form}
            remarksKey="remarks"
            baseKey="self_assessment"
            remarksMaxLength={maxLength}
            fields={coachSupportFields}
            gradeTranslations={gradeTranslationsType3}
          />
        </Stack>
      ),
    },
  ];

  const _onComplete = () => {
    modals.openConfirmModal({
      title: "Selbsteinschätzung abschliessen",
      children: <Text>Du kannst die Selbsteinschätzung nach dem Abschluss nicht mehr bearbeiten</Text>,
      labels: { confirm: "Abschliessen", cancel: "Abbrechen" },
      onConfirm: () => onComplete(form.getValues()),
    });
  };

  return (
    <Fieldset title="Details" maw="90rem">
      <form onSubmit={form.onSubmit(onSave)}>
        <Stack>
          <Accordion multiple defaultValue={accordionFields.map((f) => f.key)}>
            {accordionFields.map((field) => (
              <Accordion.Item key={field.key} value={field.key}>
                <Accordion.Control>
                  <Title order={4}>
                    {field.baseNumber}. {field.title}
                  </Title>
                </Accordion.Control>
                <Accordion.Panel>{field.render(field.baseNumber, field.key)}</Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
          <Group>
            <SaveButton label="Speichern" variant="outline" />
            <Button leftSection={<IconSend />} onClick={_onComplete}>
              Abschliessen
            </Button>
          </Group>
        </Stack>
      </form>
    </Fieldset>
  );
}
